import React from 'react'
import styled from 'styled-components'
import Published from 'Event/Dashboard/editor/views/Published'
import {useEditMode} from 'Event/EditModeProvider'
import {Agenda} from 'Event/template/Panels/Dashboard/AgendaList'
import {Draggable} from 'react-beautiful-dnd'
import {AgendaItemConfig} from 'Event/template/Panels/Dashboard/AgendaList/AgendaItemConfig'
import Configurable from 'organization/Event/Configurable'
import Grid from 'lib/ui/Grid'
import {usePanelsTemplate, usePanelsUpdate} from 'Event/template/Panels'
import AgendaEvent from 'Event/template/Panels/Dashboard/AgendaList/AgendaEvent'
import EventTimes from 'Event/template/Panels/Dashboard/AgendaList/EventTimes'
import SpeakerNames from 'Event/template/Panels/Dashboard/AgendaList/SpeakerNames'
import SpeakerImages from 'Event/template/Panels/Dashboard/AgendaList/SpeakerImageList'
import {useAttendeeVariables} from 'Event'
import {generateHashId} from 'lib/crypto/hash'
import {useAddTranslation, useTranslationLanguage} from 'Event/LanguageProvider'
import {parseVariables} from 'lib/template'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'

export default function AgendaItem(props: {
  agenda: Agenda
  id: string
  index: number
  borderColor: string
  color?: string | null
}) {
  const {agenda, id, index, borderColor, color} = props
  const isEditMode = useEditMode()
  const duplicate = useDuplicateAgenda()

  if (!isEditMode) {
    return (
      <VisibleOnMatch rules={agenda.rules}>
        <Published component={agenda}>
          <AgendaItemContainer aria-label="agenda" borderColor={borderColor}>
            <AgendaEvent agenda={agenda} color={color} />
            <Content agenda={agenda} />
          </AgendaItemContainer>
        </Published>
      </VisibleOnMatch>
    )
  }

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <Configurable
          aria-label="agenda item config"
          editableProps={{
            handleProps: provided.dragHandleProps,
            onCopy: () => duplicate(id),
          }}
        >
          <AgendaItemConfig agenda={agenda} id={id} />
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <VisibleOnMatch rules={agenda.rules}>
              <Published component={agenda}>
                <AgendaItemContainer
                  aria-label="agenda"
                  borderColor={borderColor}
                >
                  <AgendaEvent agenda={agenda} color={color} />
                  <Content agenda={agenda} />
                </AgendaItemContainer>
              </Published>
            </VisibleOnMatch>
          </div>
        </Configurable>
      )}
    </Draggable>
  )
}

function useDuplicateAgenda() {
  const template = usePanelsTemplate()
  const save = usePanelsUpdate()
  const addTranslation = useAddTranslation()
  const targetLanguage = useTranslationLanguage()

  return (sourceId: string) => {
    const source = template.agendaList.items[sourceId]
    if (!source) {
      return
    }

    const position = Object.keys(template.agendaList.items).length + 1
    const translations =
      template.localization?.translations?.[targetLanguage] ?? {}

    const resolve = (value: string | null | undefined) => {
      const [variable] = parseVariables(value ?? '')
      return variable ? translations[variable] ?? '' : value ?? ''
    }

    return generateHashId([
      'panels',
      'agenda_list',
      position.toString(),
      Date.now().toString(),
      Math.random().toString(),
    ]).then((id) => {
      const componentId = `agenda_item_${id}`

      save({
        localization: addTranslation({
          [`${componentId}_text`]: resolve(source.text),
          [`${componentId}_description`]: resolve(source.description),
          [`${componentId}_link`]: resolve(source.link),
        }),
        agendaList: {
          items: {
            [id]: {
              ...source,
              position,
              text: `{{${componentId}_text}}`,
              description: `{{${componentId}_description}}`,
              link: `{{${componentId}_link}}`,
            },
          },
        },
      })
    })
  }
}

function Content(props: {agenda: Agenda}) {
  const {agenda} = props
  const {
    speakers: {items},
  } = usePanelsTemplate()

  const v = useAttendeeVariables()

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={5}>
        <EventTimes agenda={agenda} />
      </Grid>
      <Grid item xs={12} md={3}>
        <DescriptionText>{v(agenda.description || '')}</DescriptionText>
      </Grid>
      <Grid item xs={12} md={2}>
        <SpeakerNames agenda={agenda} speakers={items} />
      </Grid>
      <Grid item xs={12} md={2}>
        <SpeakerImages agenda={agenda} speakers={items} />
      </Grid>
    </Grid>
  )
}

const AgendaItemContainer = styled.div<{borderColor: string}>`
  padding: ${(props) => `${props.theme.spacing[4]} ${props.theme.spacing[5]}`};
  margin-bottom: ${(props) => props.theme.spacing[3]};
  border-bottom: 1px solid ${(props) => props.borderColor};
  margin-bottom: 0;
`

const DescriptionText = styled.div`
  font-size: 16px;
  line-height: 170%;
  letter-spacing: 0.16px;
`
